import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X, ArrowLeft } from 'lucide-react'
import { integrationModules, getFieldsByModuleId } from './integrationModulesConfig'
import axios from '../../../config/axios'

interface PropsInterface {
  stepId: number
  closeModal: () => void
  onBack: () => void
  onSaved?: () => void
}

const AddIntegratedFieldModal = ({ stepId, closeModal, onBack, onSaved }: PropsInterface) => {
  const [selectedModule, setSelectedModule] = useState<string>('')
  const [selectedField, setSelectedField] = useState<string>('')
  const [selectedIntegrationType, setSelectedIntegrationType] = useState<string>('read')
  const [isRequired, setIsRequired] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)

  const availableFields = useMemo(() => {
    return getFieldsByModuleId(selectedModule)
  }, [selectedModule])

  const handleModuleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModule(e.target.value)
    setSelectedField('')
  }

  const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedField(e.target.value)
  }

  const isFormValid = selectedModule && selectedField && selectedIntegrationType

  const handleAddField = async () => {
    if (!isFormValid || isLoading) return

    setIsLoading(true)
    try {
      const module = integrationModules.find(m => m.id === selectedModule)
      const field = availableFields.find(f => f.field === selectedField)
      
      if (!module || !field) return

      const fieldName = `${module.name} - ${field.fieldName}`
      
      console.log('🔵 [ADD INTEGRATED FIELD] Criando campo integrado...');
      console.log('🔵 [ADD INTEGRATED FIELD] Nome do campo:', fieldName);
      console.log('🔵 [ADD INTEGRATED FIELD] Módulo:', module.name);
      console.log('🔵 [ADD INTEGRATED FIELD] Payload:', {
        name: fieldName,
        integrationConfig: {
          moduleId: selectedModule,
          moduleName: module.name,
          endpoint: module.endpoint,
          createEndpoint: module.createEndpoint,
          fieldKey: selectedField,
          fieldName: field.fieldName,
          integrationType: selectedIntegrationType,
          isRequired: isRequired
        }
      });
      
      const createFieldResponse = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`,
        {
          name: fieldName,
          integrationConfig: {
            moduleId: selectedModule,
            moduleName: module.name,
            endpoint: module.endpoint,
            createEndpoint: module.createEndpoint,
            fieldKey: selectedField,
            fieldName: field.fieldName,
            integrationType: selectedIntegrationType,
            isRequired: isRequired
          }
        }
      )

      console.log('✅ [ADD INTEGRATED FIELD] Campo criado:', createFieldResponse.data);
      console.log('🔍 [ADD INTEGRATED FIELD] Verificando estrutura do response...');
      console.log('🔍 [ADD INTEGRATED FIELD] createFieldResponse.data?.data existe?', !!createFieldResponse.data?.data);
      console.log('🔍 [ADD INTEGRATED FIELD] createFieldResponse.data?.id existe?', !!createFieldResponse.data?.id);

      const fieldId = createFieldResponse.data?.data?.id || createFieldResponse.data?.id;
      console.log('🔍 [ADD INTEGRATED FIELD] FieldId extraído:', fieldId);

      if (fieldId) {
        console.log('🔗 [ADD INTEGRATED FIELD] Vinculando campo ao step:', stepId);
        const linkResponse = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field`,
          {
            pipelineFieldId: fieldId
          }
        )
        console.log('✅ [ADD INTEGRATED FIELD] Campo vinculado ao step:', linkResponse.data);
      } else {
        console.error('❌ [ADD INTEGRATED FIELD] Não foi possível extrair o ID do campo criado!');
      }

      onSaved?.()
      closeModal()
    } catch (error) {
      console.error('❌ [ADD INTEGRATED FIELD] Erro ao adicionar campo integrado:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ModalTest>
      <S.ContainerOfModal width='500px'>
        <S.Header>
          <S.HeaderTitle>Adicionar campo integrado</S.HeaderTitle>
          <S.CloseDiv onClick={closeModal}>
            <X size={20} />
          </S.CloseDiv>
        </S.Header>

        <S.FormContainer>
          <S.InputDiv>
            <S.Label>Escolha o banco de dados</S.Label>
            <SelectStyled
              value={selectedModule}
              onChange={handleModuleChange}
            >
              <option value="">Selecione</option>
              {integrationModules.map(module => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </SelectStyled>
          </S.InputDiv>

          <S.InputDiv>
            <S.Label>Selecione o campo</S.Label>
            <SelectStyled
              value={selectedField}
              onChange={handleFieldChange}
              disabled={!selectedModule}
            >
              <option value="">Selecione</option>
              {availableFields.map(field => (
                <option key={field.field} value={field.field}>
                  {field.fieldName}
                </option>
              ))}
            </SelectStyled>
          </S.InputDiv>

          <S.InputDiv>
            <S.Label>Tipo de integração</S.Label>
            <RadioGroup>
              <RadioOption onClick={() => setSelectedIntegrationType('read')}>
                <RadioButton selected={selectedIntegrationType === 'read'} />
                <RadioLabel>Leitura</RadioLabel>
              </RadioOption>
              <RadioOption onClick={() => setSelectedIntegrationType('write')}>
                <RadioButton selected={selectedIntegrationType === 'write'} />
                <RadioLabel>Registro</RadioLabel>
              </RadioOption>
            </RadioGroup>
          </S.InputDiv>

          <SwitchContainer>
            <SwitchTitle>Solicitar preenchimento</SwitchTitle>
            <Switch active={isRequired} onClick={() => setIsRequired(!isRequired)}>
              <SwitchThumb active={isRequired}>
                {isRequired && (
                  <CheckIcon viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </CheckIcon>
                )}
              </SwitchThumb>
            </Switch>
          </SwitchContainer>
        </S.FormContainer>

        <S.BottomDiv>
          <ButtonsRow>
            <CancelText onClick={closeModal}>Cancelar</CancelText>
            <BackButton onClick={onBack}>
              <ArrowLeft size={16} /> Voltar
            </BackButton>
            <AddButton onClick={handleAddField} disabled={!isFormValid || isLoading}>
              + Adicionar campo
            </AddButton>
          </ButtonsRow>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

const SelectStyled = styled.select`
  width: 100%;
  border: none;
  outline: none;
  background-color: #EBEBEB;
  height: 2.98rem;
  padding: 0 1rem;
  border-radius: 2rem;
  font-size: 15px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`

const RadioButton = styled.div<{ selected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.selected ? '#0090D3' : '#C4C4C4'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${props => props.selected ? '#0090D3' : 'transparent'};
    transition: all 0.2s ease;
  }
`

const RadioLabel = styled.span`
  font-size: 14px;
  color: #1a1a1a;
`

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`

const SwitchTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const Switch = styled.div<{ active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
`

const SwitchThumb = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckIcon = styled.svg`
  width: 12px;
  height: 12px;
  fill: none;
  stroke: #0090D3;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
`

const ButtonsRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`

const CancelText = styled.span`
  color: #0090D3;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0.7rem 1rem;

  &:hover {
    text-decoration: underline;
  }
`

const BackButton = styled.button`
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  border-radius: 1.5rem;
  border: 1px solid #0090D3;
  color: #0090D3;
  background-color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f9ff;
  }
`

const AddButton = styled.button`
  background-color: #0090D3;
  color: #fff;
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 1.5rem;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #007ab8;
  }
`

export default AddIntegratedFieldModal
